import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostAttributeToken,
  inject,
  input,
  linkedSignal,
  output,
  TemplateRef,
  untracked,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { IPageInfo } from '@iharbeck/ngx-virtual-scroller';

import type { TellentMemberBrief } from '@core/ask-hr-types';
import { formSignalValue } from '@core/common-legacy';
import { TellentMembersDataFacade } from '@core/tellent-logic';
import type { TellentMember } from '@core/tellent-types';
import {
  AvatarComponent,
  CheckboxComponent,
  ChipsComponent,
  DropdownItemComponent,
  MultiselectFeatureDirective,
  OverlayPosition,
  SearchFeatureDirective,
  Select2Component,
  type Select2Item,
  Select2ListItemDirective,
  Select2SelectedItemDirective,
  Select2TriggerDirective,
  SelectedItemCounterDirective,
} from '@core/user-interface-legacy';
import { getInitials, uniqByKey } from '@core/utils-legacy';

export interface MemberSelectItem extends Select2Item<TellentMember['guid']> {
  avatarUrl: string | null;
  initials: string;
}

@Component({
  selector: 'team-members-multiselect',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './team-member-select.component.less',
  imports: [
    Select2Component,
    SearchFeatureDirective,
    DropdownItemComponent,
    Select2ListItemDirective,
    CheckboxComponent,
    AvatarComponent,
    Select2SelectedItemDirective,
    ChipsComponent,
    SelectedItemCounterDirective,
    MultiselectFeatureDirective,
    ReactiveFormsModule,
    NgTemplateOutlet,
    Select2TriggerDirective,
  ],
  template: `
    <rt-select2
      #select
      [items]="selectItems()"
      [hiddenItems]="preselectedItems()"
      [searchControl]="searchControl"
      [formControl]="control()"
      [overlayWidth]="overlayWidth()!"
      [overlayPosition]="overlayPosition"
      (vsUpdate)="onScrollChange($event)"
      (selection)="tempPreselectedItems.set($event)"
      withSearch
      withMultiselect
    >
      @if (trigger(); as triggerValue) {
        <ng-container
          *rtSelectTrigger="select; let inOverlay = inOverlay"
          [ngTemplateOutlet]="triggerValue"
          [ngTemplateOutletContext]="{ $implicit: select, inOverlay }"
          [ngTemplateOutletInjector]="select.injector"
        />
      }
      <rt-dropdown-item
        *rtSelectListItem="select; let item = item; let isHoverState = isHoverState; let selectCtrl"
        (click)="selectCtrl.select$.next(item)"
        [class.hover]="isHoverState"
        class="team-member-select-item"
      >
        <rt-checkbox
          prefix
          [checked]="selectCtrl.itemSelector.isSelected(item)"
          [hasMargin]="false"
          (valueChange)="selectCtrl.select$.next(item)"
        />
        <rt-avatar
          [type]="item.avatarUrl ? 'image' : 'initials'"
          size="small-x"
          [imageSrc]="item.avatarUrl ?? null"
          [initials]="item.initials"
          class="dropdown-avatar"
          margin-right-small-x
        />
        {{ item.label }}
      </rt-dropdown-item>
      <rt-chips
        *rtSelectSelectedItem="select; let remove = remove; let item = item"
        [text]="item.label"
        [showClose]="true"
        (close)="$event.stopPropagation(); remove(item)"
      >
        <rt-avatar
          size="small-x"
          [type]="item.avatarUrl ? 'image' : 'initials'"
          [initials]="item.initials"
          [imageSrc]="item.avatarUrl!"
          prefix
        />
      </rt-chips>
      <rt-chips
        *rtSelectSelectedItemCounter="let hiddenCount = hiddenCount"
        [text]="'+' + hiddenCount"
        [showClose]="false"
      />
    </rt-select2>
  `,
})
export class TeamMemberSelectComponent {
  private readonly LOAD_MORE_WHEN_LEFT = 10;
  private readonly PAGE_SIZE = 40;
  private readonly tellentMembersDataFacade = inject(TellentMembersDataFacade);
  private readonly selectorScope = inject(new HostAttributeToken('selectorScope'));

  protected readonly overlayPosition: OverlayPosition[] = [OverlayPosition.BottomLeft];

  public control = input.required<FormControl<string[]>>();
  public selectedMembers = input<TellentMemberBrief[]>([]);
  public trigger = input<TemplateRef<any> | null>(null);
  public overlayWidth = input<number | null>(null);

  public selectedMembersChange = output<TellentMemberBrief[]>();

  protected searchControl = new FormControl('', { nonNullable: true });
  protected members = this.tellentMembersDataFacade.teamMembersPaginated(this.selectorScope);
  protected selectItems = computed((): MemberSelectItem[] =>
    this.members().rows.map(member => this.memberToSelectItem(member)),
  );
  protected tempPreselectedItems = signal<MemberSelectItem[]>([]);
  protected preselectedItems = computed(
    (): MemberSelectItem[] =>
      uniqByKey(
        [
          ...this.selectedMembers().map(member => this.memberToSelectItem(member)),
          ...this.tempPreselectedItems(),
        ],
        'value',
      ),
    { equal: (a, b) => a.length === b.length && a.every((v, i) => v.value === b[i].value) },
  );

  private controlValue = formSignalValue(this.control);
  private searchValue = formSignalValue(this.searchControl);
  private currentPage = linkedSignal({
    source: this.searchValue,
    computation: () => 1,
  });

  private itemsLoader = effect(() => {
    const page = this.currentPage();
    const q = this.searchValue();
    untracked(() =>
      this.tellentMembersDataFacade.fetchAppMembersPaginated(
        { scope: this.selectorScope, page, limit: this.PAGE_SIZE, q },
        page !== 1,
      ),
    );
  });

  private emitSelectedMembersChange = effect(() => {
    const allMembers = [...this.selectedMembers(), ...this.members().rows];
    const selectedMembers = this.controlValue()
      .map(guid => allMembers.find(member => member.guid === guid))
      .filter(v => !!v);
    this.selectedMembersChange.emit(selectedMembers);
  });

  protected onScrollChange(event: IPageInfo) {
    const { page, limit, totalCount } = this.members().meta;
    if (!page || !limit || !totalCount) return;
    const isCloseToScrollEnd =
      event.endIndexWithBuffer > this.members().rows.length - this.LOAD_MORE_WHEN_LEFT;
    const hasMore = page * limit < totalCount;
    const notLoading = page === this.currentPage();
    if (isCloseToScrollEnd && hasMore && notLoading) {
      this.currentPage.update(currentPage => currentPage + 1);
    }
  }

  protected memberToSelectItem(member: TellentMemberBrief): MemberSelectItem {
    return {
      label: `${member.firstName} ${member.lastName}`,
      value: member.guid,
      avatarUrl: member.avatarThumbUrl,
      initials: getInitials(member.firstName, member.lastName),
    };
  }
}
